import { useEffect, useState, type FormEvent } from 'react';
import { prepareQuestions, engineName, type ProbeEngine } from '../api';
import { listQuerySets, saveQuerySetFor, type QuerySet } from '../account';
import SetSchedule from './SetSchedule';

export type Engine = 'openai' | 'gemini';
export interface WizardChoice { url: string; set: QuerySet; engine: Engine }

type Step = 1 | 2 | 3 | 4;
const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: 'Site' },
  { n: 2, label: 'Questions' },
  { n: 3, label: 'AI engine' },
  { n: 4, label: 'Run' },
];

/** What a buyer would type, one per line, without blanks or repeats. */
const toLines = (text: string): string[] => {
  const seen = new Set<string>();
  return text.split('\n').map((l) => l.trim()).filter((l) => {
    const k = l.toLowerCase();
    if (!l || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};
const hostOf = (u: string): string => {
  const t = u.trim();
  try { return new URL(/^https?:\/\//i.test(t) ? t : `https://${t}`).hostname.replace(/^www\./, '').toLowerCase(); } catch { return t; }
};
const splitList = (v: string): string[] => v.split(/[,\n;]/).map((x) => x.trim()).filter(Boolean).slice(0, 10);

/**
 * The scan, as four plain steps: which site, which questions, which AI, run.
 *
 * It replaces an input bar that carried a domain picker, a set picker, an engine
 * toggle and a Scan button side by side, followed by a hints form and a review
 * panel that appeared below the fold. Every one of those was a decision, all
 * on screen at once, and nothing said which came first.
 *
 * The questions are the client's. Writing them is the first option; the
 * generator is an opt-in helper behind its own button, and it says how it works
 * before it runs, because a generated set reads like the site, not like the
 * buyer (measured: it missed seven of the eight questions that had ever won).
 */
export default function ScanWizard({
  signedIn, plan, engines, scannedDomains, freeCap, initial, busy, onRun, onNeedSignIn, onClose,
}: {
  signedIn: boolean;
  plan: 'free' | 'pro';
  engines: ProbeEngine[];
  scannedDomains: string[];
  freeCap: number;
  initial?: { url?: string; setId?: string | null; engine?: Engine };
  busy: boolean;
  onRun: (choice: WizardChoice) => void;
  onNeedSignIn: () => void;
  /** Present when there is a result behind the wizard to go back to. */
  onClose?: () => void;
}): JSX.Element {
  const [step, setStep] = useState<Step>(1);
  const [url, setUrl] = useState(initial?.url ?? '');
  const [engine, setEngine] = useState<Engine>(initial?.engine ?? 'openai');

  // Step 2 state.
  const [sets, setSets] = useState<QuerySet[] | null>(null);
  const [setId, setSetId] = useState<string | null>(initial?.setId ?? null);
  const [mode, setMode] = useState<'pick' | 'edit' | 'saved'>('pick');
  const [draft, setDraft] = useState<{ id?: string; name: string; text: string }>({ name: '', text: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // The opt-in generator.
  const [helper, setHelper] = useState(false);
  const [hints, setHints] = useState({ rivals: '', terms: '', notUs: '' });
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState<string | null>(null);

  const domain = hostOf(url);
  const chosen = sets?.find((s) => s.id === setId) ?? null;
  const cap = plan === 'pro' ? 25 : freeCap;
  const available = engines.length ? engines : [{ engine: 'openai' as const, provider: 'openai', name: 'ChatGPT', label: 'ChatGPT' }];

  // ChatGPT first and preselected; Gemini as the alternative.
  const ordered = [...available].sort((a, b) => (a.engine === 'openai' ? -1 : b.engine === 'openai' ? 1 : 0));
  useEffect(() => {
    if (!available.some((e) => e.engine === engine)) setEngine(ordered[0]!.engine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engines]);

  async function loadSets(): Promise<void> {
    setSets(null);
    try {
      const d = await listQuerySets(url.trim());
      setSets(d.sets);
      const keep = d.sets.find((s) => s.id === setId);
      setSetId(keep?.id ?? d.active?.id ?? d.sets[0]?.id ?? null);
      if (d.sets.length === 0) startNew(true);
      else setMode('pick');
    } catch {
      setSets([]);
      startNew(true);
    }
  }

  function startNew(first = false): void {
    setDraft({ name: first ? 'My questions' : '', text: '' });
    setHelper(false);
    setGenMsg(null);
    setMsg(null);
    setMode('edit');
  }

  function goSite(e?: FormEvent): void {
    e?.preventDefault();
    if (!url.trim()) return;
    if (!signedIn) { onNeedSignIn(); return; }
    setStep(2);
    void loadSets();
  }

  async function suggest(): Promise<void> {
    if (generating) return;
    setGenerating(true);
    setGenMsg(null);
    try {
      const h = {
        ...(splitList(hints.rivals).length ? { rivals: splitList(hints.rivals) } : {}),
        ...(splitList(hints.terms).length ? { terms: splitList(hints.terms) } : {}),
        ...(hints.notUs.trim() ? { notUs: hints.notUs.trim() } : {}),
      };
      const r = await prepareQuestions(url.trim(), plan === 'pro' ? undefined : freeCap, true, h);
      if (r.skipped) {
        setGenMsg(r.skipped === 'ai-blocked'
          ? 'This site blocks the AI crawlers in robots.txt, so there is nothing for AI to read and no suggestions to make.'
          : 'We could not read enough of this site to suggest anything (an error page, a login or a consent wall). Write your own questions instead.');
        return;
      }
      const merged = toLines([draft.text, ...r.questions].join('\n'));
      setDraft((d) => ({ ...d, name: d.name || 'Suggested questions', text: merged.join('\n') }));
      setGenMsg(`${r.questions.length} suggestions added below. Read every one: delete what doesn't fit, reword what your buyers would say differently.`);
      setHelper(false);
    } catch (err) {
      setGenMsg(err instanceof Error ? err.message : 'could not write suggestions');
    } finally {
      setGenerating(false);
    }
  }

  async function saveDraft(): Promise<void> {
    const queries = toLines(draft.text);
    if (queries.length === 0) { setMsg('Add at least one question.'); return; }
    setSaving(true);
    setMsg(null);
    try {
      const r = await saveQuerySetFor({
        id: draft.id ? Number(draft.id) : undefined,
        url: url.trim(),
        name: draft.name.trim() || 'My questions',
        queries,
        activate: true,
      });
      const d = await listQuerySets(url.trim());
      setSets(d.sets);
      setSetId(r.set.id);
      setMode('saved');
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'could not save');
    } finally {
      setSaving(false);
    }
  }

  const lineCount = toLines(draft.text).length;
  const primary = 'bg-cyan text-warm-dark font-medium rounded-lg px-6 py-2.5 text-sm hover:opacity-90 disabled:opacity-40 transition';
  const secondary = 'font-mono text-sm text-text-secondary border border-warm-dark-2 rounded-lg px-4 py-2.5 hover:text-cyan hover:border-cyan/50 disabled:opacity-40 transition';
  const input = 'w-full bg-warm-dark border border-warm-dark-2 focus:border-cyan/60 rounded-lg px-3 py-2 text-sm text-text-primary outline-none';

  return (
    <section className="w-full max-w-3xl rounded-2xl bg-warm-dark-2/40 ring-1 ring-cyan/20 shadow-xl shadow-black/30 p-5 sm:p-7">
      {/* The steps, always visible, so it is clear where you are and how far is left. */}
      <div className="flex items-center justify-between gap-3 mb-6">
        <ol className="flex items-center gap-1.5 sm:gap-3 flex-wrap">
          {STEPS.map((s, i) => {
            const done = s.n < step;
            const here = s.n === step;
            return (
              <li key={s.n} className="flex items-center gap-1.5 sm:gap-3">
                <button
                  type="button"
                  disabled={!done || busy}
                  onClick={() => setStep(s.n)}
                  className={`flex items-center gap-2 font-mono text-xs sm:text-sm ${here ? 'text-cyan' : done ? 'text-text-primary hover:text-cyan' : 'text-text-secondary/60'}`}
                >
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs border ${
                    here ? 'border-cyan bg-cyan/15' : done ? 'border-cyan/50 text-cyan' : 'border-warm-dark-2'}`}>
                    {done ? '✓' : s.n}
                  </span>
                  <span className={here ? '' : 'hidden sm:inline'}>{s.label}</span>
                </button>
                {i < STEPS.length - 1 && <span className="w-4 sm:w-8 h-px bg-warm-dark-2" />}
              </li>
            );
          })}
        </ol>
        {onClose && (
          <button type="button" onClick={onClose} className="font-mono text-xs text-text-secondary hover:text-cyan shrink-0">
            ✕ close
          </button>
        )}
      </div>

      {/* ── 1. Site ─────────────────────────────────────────────── */}
      {step === 1 && (
        <form onSubmit={goSite}>
          <h2 className="font-serif text-2xl mb-1">Which site do you want to check?</h2>
          <p className="text-sm text-text-secondary mb-4">A domain or the exact page, e.g. your main service page.</p>
          <input
            autoFocus
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="domain.com"
            className="w-full bg-warm-dark border border-warm-dark-2 focus:border-cyan/60 rounded-xl px-5 py-4 text-text-primary placeholder:text-text-secondary outline-none font-mono text-lg"
          />
          {scannedDomains.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mt-3">
              <span className="font-mono text-xs text-text-secondary">your sites:</span>
              {scannedDomains.slice(0, 8).map((d) => (
                <button key={d} type="button" onClick={() => setUrl(`https://${d}/`)}
                  className={`font-mono text-xs border rounded-full px-3 py-1 transition ${
                    domain === d ? 'text-cyan border-cyan/60 bg-cyan/10' : 'text-text-secondary border-warm-dark-2 hover:text-cyan hover:border-cyan/50'}`}>
                  {d}
                </button>
              ))}
            </div>
          )}
          <div className="flex justify-end mt-6">
            <button type="submit" disabled={!url.trim()} className={primary}>Next: questions →</button>
          </div>
        </form>
      )}

      {/* ── 2. Questions ────────────────────────────────────────── */}
      {step === 2 && (
        <div>
          <h2 className="font-serif text-2xl mb-1">What should AI be asked?</h2>
          <p className="text-sm text-text-secondary mb-4">
            The questions your buyers would type into ChatGPT or Google, about <span className="text-text-primary font-mono">{domain}</span>.
          </p>

          {sets === null && <p className="font-mono text-sm text-text-secondary">Loading your question sets…</p>}

          {/* Pick a saved set */}
          {sets && mode === 'pick' && (
            <>
              <div className="flex flex-col gap-2" role="radiogroup" aria-label="question set">
                {sets.map((s) => (
                  <label key={s.id} className={`flex items-start gap-3 border rounded-xl px-4 py-3 cursor-pointer transition ${
                    setId === s.id ? 'border-cyan/60 bg-cyan/5' : 'border-warm-dark-2 hover:border-cyan/30'}`}>
                    <input type="radio" name="set" checked={setId === s.id} onChange={() => setSetId(s.id)} className="mt-1 accent-[#00FFD4]" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-text-primary">{s.name}</span>
                      <span className="block font-mono text-xs text-text-secondary">
                        {s.queries.length} questions · updated {new Date(s.updatedAt).toLocaleDateString()}
                      </span>
                      {setId === s.id && (
                        <span className="block text-sm text-text-secondary mt-1.5 truncate">
                          {s.queries.slice(0, 2).join(' · ')}{s.queries.length > 2 ? ' · …' : ''}
                        </span>
                      )}
                    </span>
                    {setId === s.id && (
                      <button type="button"
                        onClick={(e) => { e.preventDefault(); setDraft({ id: s.id, name: s.name, text: s.queries.join('\n') }); setHelper(false); setGenMsg(null); setMsg(null); setMode('edit'); }}
                        className="font-mono text-xs text-text-secondary hover:text-cyan shrink-0">edit</button>
                    )}
                  </label>
                ))}
              </div>
              <div className="flex items-center justify-between gap-3 flex-wrap mt-6">
                <button type="button" onClick={() => startNew()} className={secondary}>+ New question set</button>
                <button type="button" disabled={!chosen} onClick={() => setStep(3)} className={primary}>Next: AI engine →</button>
              </div>
            </>
          )}

          {/* Write (or edit) a set */}
          {sets && mode === 'edit' && (
            <>
              <label className="flex flex-col gap-1 mb-3">
                <span className="font-mono text-xs text-text-secondary">Name of this set</span>
                <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="e.g. Fulfilment questions" className={input} />
              </label>
              <textarea
                value={draft.text}
                onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                rows={9}
                spellCheck={false}
                placeholder={'One question per line, in your buyers’ language.\n\nbest accounting software for small agencies\nhow do I connect my webshop to my accounting system?'}
                className="w-full bg-warm-dark border border-warm-dark-2 focus:border-cyan/60 rounded-lg px-3 py-2 text-sm text-text-primary outline-none font-mono leading-relaxed"
              />
              <p className="font-mono text-xs text-text-secondary mt-1">
                {lineCount} question{lineCount === 1 ? '' : 's'}
                {lineCount > cap && <span className="text-estimated">{plan === 'pro' ? ` · a run asks the first ${cap}` : ` · free scans ask the first ${cap}`}</span>}
              </p>

              {/* The generator: opt-in, and it says how it works first. */}
              {!helper ? (
                <button type="button" onClick={() => { setHelper(true); setGenMsg(null); }}
                  className="mt-3 font-mono text-sm text-cyan hover:underline">
                  Not sure what to ask? Get suggestions from your pages →
                </button>
              ) : (
                <div className="mt-3 border border-estimated/40 bg-estimated/5 rounded-xl p-4">
                  <p className="text-sm text-text-primary mb-1">How the suggestions are made</p>
                  <p className="text-sm text-text-secondary mb-3">
                    We read up to 14 pages of {domain}, pick out your products, services and the words in your page titles
                    and URLs, and an AI model writes questions a buyer might ask. It can&apos;t know the words your buyers
                    really use or which topics you can actually win, so <span className="text-text-primary">treat it as a draft</span>:
                    delete what doesn&apos;t fit and add what&apos;s missing. Nothing is searched yet, and it costs you nothing.
                  </p>
                  <p className="font-mono text-xs text-text-secondary mb-2">Optional, but it makes the suggestions much better:</p>
                  <div className="grid sm:grid-cols-3 gap-2">
                    <input value={hints.rivals} onChange={(e) => setHints({ ...hints, rivals: e.target.value })}
                      placeholder="Competitors (comma-separated)" className={input} />
                    <input value={hints.terms} onChange={(e) => setHints({ ...hints, terms: e.target.value })}
                      placeholder="Words your buyers use" className={input} />
                    <input value={hints.notUs} onChange={(e) => setHints({ ...hints, notUs: e.target.value })}
                      placeholder="What you do NOT offer" className={input} />
                  </div>
                  <div className="flex items-center gap-3 mt-3 flex-wrap">
                    <button type="button" onClick={() => void suggest()} disabled={generating} className={primary}>
                      {generating ? 'Reading your pages…' : 'Suggest questions'}
                    </button>
                    <button type="button" onClick={() => setHelper(false)} disabled={generating}
                      className="font-mono text-sm text-text-secondary hover:text-cyan">cancel</button>
                    {generating && <span className="font-mono text-xs text-text-secondary">usually 20–40 seconds</span>}
                  </div>
                </div>
              )}
              {genMsg && <p className="text-sm text-text-secondary mt-2">{genMsg}</p>}

              <div className="flex items-center justify-between gap-3 flex-wrap mt-6">
                {sets.length > 0
                  ? <button type="button" onClick={() => setMode('pick')} className={secondary}>← Back to my sets</button>
                  : <span />}
                <button type="button" onClick={() => void saveDraft()} disabled={saving || lineCount === 0} className={primary}>
                  {saving ? 'Saving…' : draft.id ? 'Save changes' : 'Save question set'}
                </button>
              </div>
              {msg && <p className="font-mono text-sm text-[#E0574A] mt-2">{msg}</p>}
            </>
          )}

          {/* Saved: say why more than one set is worth having, once, here. */}
          {sets && mode === 'saved' && chosen && (
            <>
              <p className="text-text-primary mb-4">
                <span className="text-cyan">✓</span> Saved &ldquo;{chosen.name}&rdquo; with {chosen.queries.length} questions.
              </p>
              <div className="border border-warm-dark-2 rounded-xl p-4">
                <p className="text-text-primary mb-1">Tip: you can keep more than one question set</p>
                <p className="text-sm text-text-secondary mb-2">One list rarely covers everything you sell. Separate sets help when you want to:</p>
                <ul className="text-sm text-text-secondary list-disc pl-5 space-y-1">
                  <li>measure each <span className="text-text-primary">product or service line</span> on its own,</li>
                  <li>track each <span className="text-text-primary">market or language</span> separately,</li>
                  <li>compare <span className="text-text-primary">questions with your brand name</span> against generic ones where buyers don&apos;t know you yet.</li>
                </ul>
                <p className="text-sm text-text-secondary mt-2">
                  Each set keeps its own trend and can have its own automatic re-check, so you see where you win and where you don&apos;t.
                </p>
              </div>
              <div className="flex items-center justify-between gap-3 flex-wrap mt-6">
                <button type="button" onClick={() => startNew()} className={secondary}>+ Create another set</button>
                <button type="button" onClick={() => setStep(3)} className={primary}>Continue with this set →</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── 3. Engine ───────────────────────────────────────────── */}
      {step === 3 && (
        <div>
          <h2 className="font-serif text-2xl mb-1">Which AI should we ask?</h2>
          <p className="text-sm text-text-secondary mb-4">You can run the same questions on the other one later and compare.</p>
          <div className="grid sm:grid-cols-2 gap-3" role="radiogroup" aria-label="AI engine">
            {ordered.map((e) => {
              const on = engine === e.engine;
              return (
                <button key={e.engine} type="button" role="radio" aria-checked={on} onClick={() => setEngine(e.engine)}
                  className={`text-left border rounded-xl p-4 transition ${on ? 'border-cyan/60 bg-cyan/5' : 'border-warm-dark-2 hover:border-cyan/30'}`}>
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-text-primary text-lg">{e.engine === 'openai' ? 'ChatGPT' : engineName(e.engine)}</span>
                    {e.engine === 'openai' && <span className="font-mono text-[10px] uppercase tracking-wide text-cyan">default</span>}
                  </span>
                  <span className="block text-sm text-text-secondary mt-1">
                    {e.engine === 'openai'
                      ? 'OpenAI’s assistant with live web search. The one most buyers ask.'
                      : 'Google’s model, grounded in Google Search. Close to what AI Overviews show.'}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex justify-end mt-6">
            <button type="button" onClick={() => setStep(4)} className={primary}>Next: review →</button>
          </div>
        </div>
      )}

      {/* ── 4. Run ──────────────────────────────────────────────── */}
      {step === 4 && chosen && (
        <div>
          <h2 className="font-serif text-2xl mb-4">Ready to scan</h2>
          <dl className="border border-warm-dark-2 rounded-xl divide-y divide-warm-dark-2">
            {([
              ['Site', domain, 1],
              ['Questions', `${chosen.name} · ${Math.min(chosen.queries.length, cap)}${chosen.queries.length > cap ? ` of ${chosen.queries.length}` : ''} questions`, 2],
              ['AI engine', engine === 'openai' ? 'ChatGPT' : engineName(engine), 3],
            ] as const).map(([k, v, s]) => (
              <div key={k} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <dt className="font-mono text-xs text-text-secondary w-24 shrink-0">{k}</dt>
                <dd className="flex-1 min-w-0 text-text-primary truncate">{v}</dd>
                <button type="button" onClick={() => setStep(s)} disabled={busy} className="font-mono text-xs text-text-secondary hover:text-cyan">change</button>
              </div>
            ))}
          </dl>

          <ol className="text-sm text-text-secondary mt-4 space-y-1 list-decimal pl-5">
            <li>We check whether AI can read and understand this page. About a minute.</li>
            <li>
              We ask {engine === 'openai' ? 'ChatGPT' : engineName(engine)} your questions with live web search. A few minutes;
              you can leave the page, the results are saved and emailed to you.
            </li>
          </ol>

          {plan === 'pro' && (
            <div className="mt-4 border border-warm-dark-2 rounded-xl px-4 py-3">
              <p className="text-sm text-text-primary mb-2">Re-check this set automatically <span className="text-text-secondary">(optional)</span></p>
              <SetSchedule set={chosen} plan={plan} defaultEngine={engine} />
            </div>
          )}

          <div className="flex justify-end mt-6">
            <button type="button" disabled={busy} onClick={() => onRun({ url: url.trim(), set: chosen, engine })}
              className="bg-cyan text-warm-dark font-medium rounded-xl px-8 py-3.5 text-base hover:opacity-90 disabled:opacity-40 transition">
              {busy ? 'Scanning…' : 'Run scan'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
